// SplashScreen — brief logo overlay shown once on app launch.
//
// Why: gives the user a fraction-of-a-second branded moment while
// React mounts, IDB hydrates, and the map style/tiles begin loading.
// Without it, the first paint is a slightly disorienting chunk of
// blank dark-grey shell.
//
// Behavior:
//   • Renders a centered Fyrefly logo + wordmark over the app's dark
//     background.
//   • Auto-fades after `MIN_VISIBLE_MS` (so a fast device still sees
//     the brand for at least that long) — fade itself takes
//     `FADE_MS`.
//   • Dismisses immediately on any pointer/key event (so it never
//     blocks interaction).
//   • Shows only once per browser session: we set a sessionStorage
//     flag after first display. Reload reshows; tab persistence
//     across navigation does not.

import { useEffect, useState } from 'react';

const MIN_VISIBLE_MS = 900;
const FADE_MS = 350;
const SESSION_FLAG = 'fyrefly.splash.shown.v1';

export default function SplashScreen() {
  const [shown, setShown] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(SESSION_FLAG) !== '1';
    } catch {
      return true;
    }
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!shown) return;
    try {
      sessionStorage.setItem(SESSION_FLAG, '1');
    } catch {
      /* private mode / disabled — fine, splash just shows again */
    }
    const dismissTimer = window.setTimeout(() => setFading(true), MIN_VISIBLE_MS);
    const removeTimer = window.setTimeout(() => setShown(false), MIN_VISIBLE_MS + FADE_MS);
    function dismissEarly() {
      window.clearTimeout(dismissTimer);
      setFading(true);
      window.setTimeout(() => setShown(false), FADE_MS);
    }
    window.addEventListener('pointerdown', dismissEarly, { once: true });
    window.addEventListener('keydown', dismissEarly, { once: true });
    return () => {
      window.clearTimeout(dismissTimer);
      window.clearTimeout(removeTimer);
      window.removeEventListener('pointerdown', dismissEarly);
      window.removeEventListener('keydown', dismissEarly);
    };
  }, [shown]);

  if (!shown) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        backgroundColor: '#0b0d0f',
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        pointerEvents: 'none',
      }}
    >
      <img
        src="/icon-512.png"
        alt="Fyrefly"
        width={132}
        height={132}
        style={{
          width: 132,
          height: 132,
          borderRadius: 28,
          boxShadow: '0 0 60px rgba(255, 90, 31, 0.35)',
          animation: 'fyrefly-splash-pulse 1.6s ease-in-out infinite',
        }}
      />
      <div
        style={{
          color: '#d6dbe2',
          letterSpacing: '0.4em',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        Fyrefly
      </div>
      <style>{`
        @keyframes fyrefly-splash-pulse {
          0%, 100% { transform: scale(1);    filter: drop-shadow(0 0 18px rgba(255, 90, 31, 0.35)); }
          50%      { transform: scale(1.04); filter: drop-shadow(0 0 28px rgba(255, 90, 31, 0.55)); }
        }
      `}</style>
    </div>
  );
}
